import type { Middleware, MiddlewareAPI, UnknownAction } from "@reduxjs/toolkit";

import { isRejectedWithValue } from "@reduxjs/toolkit";

import classifyTenantInactiveError from "~/helpers/classifyTenantInactiveError";
import classifyTrialLimitError from "~/helpers/classifyTrialLimitError";
import mapGraphqlErrorToMessage from "~/helpers/graphqlErrorMessages";
import trialLimitSnackGate from "~/helpers/trialLimitSnackGate";
import normalizeApiError, {
	formatApiErrorHeadline,
	type NormalizedApiError
} from "~/store/middleware/normalizeApiError";
import { tenantInactiveDetected } from "~/store/tenantAccessSlice";
import { trialLimitReached } from "~/store/trialLimitSlice";

const routeTenantInactive = (api: MiddlewareAPI, normalized: NormalizedApiError): void => {
	const classification = classifyTenantInactiveError(normalized);
	if (!classification) return;
	const alreadyInactive = (api.getState() as { tenantAccess?: { inactive?: boolean } }).tenantAccess?.inactive;
	if (alreadyInactive) return;
	api.dispatch(tenantInactiveDetected({ expiredTrial: classification.expiredTrial }));
};

const routeTrialLimit = (api: MiddlewareAPI, normalized: NormalizedApiError): void => {
	const classification = classifyTrialLimitError(normalized);
	if (!classification) return;
	trialLimitSnackGate.armForNextErrorSnack();
	api.dispatch(trialLimitReached(classification));
};

// Helper function to check if error is a network request failure
function isNetworkError(payload: unknown): boolean {
	if (payload && typeof payload === "object" && payload !== null) {
		if ("status" in payload && (payload.status === "FETCH_ERROR" || payload.status === "TIMEOUT_ERROR")) {
			return true;
		}

		// Check error message
		if ("message" in payload && typeof payload.message === "string") {
			return (
				payload.message.includes("Network request failed") ||
				payload.message.includes("fetch is not defined") ||
				payload.message.includes("Failed to fetch")
			);
		}

		// Check data.message for GraphQL errors
		if (
			"data" in payload &&
			payload.data &&
			typeof payload.data === "object" &&
			payload.data !== null &&
			"message" in payload.data &&
			typeof payload.data.message === "string"
		) {
			return (
				payload.data.message.includes("Network request failed") ||
				payload.data.message.includes("fetch is not defined") ||
				payload.data.message.includes("Failed to fetch")
			);
		}

		// Check nested error structures
		if (
			"error" in payload &&
			payload.error &&
			typeof payload.error === "object" &&
			payload.error !== null &&
			"message" in payload.error
		) {
			return (
				typeof payload.error.message === "string" &&
				(payload.error.message.includes("Network request failed") ||
					payload.error.message.includes("fetch is not defined") ||
					payload.error.message.includes("Failed to fetch"))
			);
		}
	}

	return false;
}

const shouldShowSnack = (action: { meta?: unknown }): boolean => {
	const meta = action.meta as { arg?: { originalArgs?: { showSnackOnError?: boolean } } } | undefined;
	return meta?.arg?.originalArgs?.showSnackOnError !== false;
};

// Mutation rejections owned by calling helper (openSnack on catch).
// Mutations opt in to the bridge via __showBridgeSnack: true.
const shouldSurfaceForMutation = (action: { meta?: unknown }): boolean => {
	const meta = action.meta as { arg?: { originalArgs?: { __showBridgeSnack?: boolean } } } | undefined;
	return meta?.arg?.originalArgs?.__showBridgeSnack === true;
};

const isMutationAction = (action: { meta?: unknown }): boolean => {
	const meta = action.meta as { arg?: { type?: string } } | undefined;
	return meta?.arg?.type === "mutation";
};

interface BaseQueryMeta {
	response?: { errors?: unknown };
}

interface RtkActionLike extends UnknownAction {
	meta?: {
		arg?: {
			endpointName?: string;
			originalArgs?: { showSnackOnError?: boolean; __showBridgeSnack?: boolean };
			type?: string;
		};
		baseQueryMeta?: BaseQueryMeta;
	} & Record<string, unknown>;
	payload?: Record<string, unknown> & { error?: boolean; message?: unknown };
}

// graphql-request-base-query strips response off the payload onto meta.baseQueryMeta —
// merge it back so normalizeApiError finds response.errors for code mapping.
const mergeBaseQueryResponse = (
	payload: Record<string, unknown>,
	baseQueryMeta: BaseQueryMeta | undefined
): Record<string, unknown> => {
	if (!baseQueryMeta?.response || "response" in payload) return payload;
	return { ...payload, response: baseQueryMeta.response };
};

export const errorLoggingMiddleware: Middleware = (api: MiddlewareAPI) => (next) => (rawAction) => {
	const action = rawAction as RtkActionLike;
	const rawPayload = action.payload;
	if (isRejectedWithValue(action) && rawPayload) {
		rawPayload.error = true;
		const endpointName: string | null = action.meta?.arg?.endpointName ?? null;
		const payload = mergeBaseQueryResponse(rawPayload, action.meta?.baseQueryMeta);
		const networkError = isNetworkError(payload);
		const normalized = normalizeApiError(payload, endpointName);
		routeTenantInactive(api, normalized);
		routeTrialLimit(api, normalized);
		// null when caller opts out, network failure, silent mutation, or no mapped code.
		// The snack bridge skips errors with userMessage === null.
		const mutationSilent = isMutationAction(action) && !shouldSurfaceForMutation(action);
		const userMessage =
			!shouldShowSnack(action) || networkError || mutationSilent ? null : mapGraphqlErrorToMessage(normalized);
		const errorDetail = {
			code: endpointName || "Unknown",
			detail: typeof payload.message === "string" ? payload.message : "",
			message: "Api Error",
			userMessage,
			// Mark network errors as viewed to prevent notification spam
			viewed: networkError
		};
		api.dispatch({ payload: errorDetail, type: "error/addError" });

		// Only log errors that are not network connection failures.
		// Use console.error directly — the baseQuery/sentry middleware instrumentation
		// already captures these to Sentry and api.dispatch above adds them to Redux.
		// Build a structured headline so wrappers that toString the second arg
		// still surface useful info instead of "[object Object]".
		if (!networkError) {
			console.error(`API Error caught by middleware: ${formatApiErrorHeadline(normalized)}`, normalized);
		}
	}
	const endpointName: string | null = action.meta?.arg?.endpointName ?? null;
	if (endpointName && rawPayload) {
		const endpointPayload = rawPayload[endpointName] as { errors?: unknown[] } | undefined;
		if (endpointPayload?.errors?.length) {
			rawPayload.error = true;
			const networkError = isNetworkError(rawPayload);
			const normalized = normalizeApiError(rawPayload, endpointName);
			routeTenantInactive(api, normalized);
			routeTrialLimit(api, normalized);
			const mutationSilent = isMutationAction(action) && !shouldSurfaceForMutation(action);
			const userMessage =
				!shouldShowSnack(action) || networkError || mutationSilent ? null : mapGraphqlErrorToMessage(normalized);
			const errorDetail = {
				code: endpointName,
				detail: endpointPayload.errors as object,
				message: "Api Payload Error",
				userMessage,
				// Mark network errors as viewed to prevent notification spam
				viewed: networkError
			};
			api.dispatch({ payload: errorDetail, type: "error/addError" });

			// Only log errors that are not network connection failures.
			// Use console.error directly — the baseQuery Sentry instrumentation already
			// captures these to Sentry and api.dispatch above already adds them to the
			// Redux error log.
			if (!networkError) {
				console.error(`API Payload Error caught by middleware: ${formatApiErrorHeadline(normalized)}`, normalized);
			}
		}
	}
	return next(action);
};
